import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ApartmentCardProps, ApartmentStatus } from '../types/Apartment';
import { Building2, BedDouble, MapPin, ChevronRight, ChevronDown, Info, X } from 'lucide-react';
import { formatPrice } from '../config/priceConfig';

const Card = styled.div<{ isSelected: boolean; isExpanded?: boolean; isMobile?: boolean }>`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 0;
  box-shadow: ${props => props.isSelected
    ? '0 0 0 2px #2563eb, 0 4px 12px rgba(0, 0, 0, 0.1)'
    : '0 2px 8px rgba(0, 0, 0, 0.05)'
  };
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.isSelected ? '#2563eb' : '#e5e7eb'};
  position: relative;
  overflow: hidden;

  &:hover {
    transform: ${props => props.isMobile ? 'none' : 'translateY(-4px)'};
    box-shadow: ${props => props.isMobile ? '0 2px 8px rgba(0, 0, 0, 0.05)' : '0 4px 12px rgba(0, 0, 0, 0.1)'};
    border-color: ${props => props.isMobile ? '#e5e7eb' : '#d1d5db'};
  }
  
  @media (max-width: 850px) {
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
    }
  }
`;

const ThumbnailImage = styled.div<{ src: string; isCollapsed?: boolean }>`
  width: 100%;
  height: ${props => props.isCollapsed ? '0' : '180px'};
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: 12px 12px 0 0;
  position: relative;
  transition: height 0.3s ease;
  overflow: hidden;
  
  @media (max-width: 850px) {
    height: ${props => props.isCollapsed ? '0' : '160px'};
  }
`;

const CardContent = styled.div<{ isCollapsed?: boolean }>`
  position: relative;
  padding: ${props => props.isCollapsed ? '16px' : '20px'};
  transition: padding 0.3s ease;
  
  @media (max-width: 850px) {
    padding: ${props => props.isCollapsed ? '12px 16px' : '16px'};
  }
`;

const Status = styled.div<{ status: ApartmentStatus }>`
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: ${props => {
    switch (props.status) {
      case 'available': return '#10b981'; // Grün für verfügbar
      case 'reserved': return '#f39c12'; // Orange für reserviert  
      case 'sold': return '#ef4444'; // Rot für verkauft
      default: return '#10b981';
    }
  }};
  color: white;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  z-index: 3;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const CollapsedHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  width: 100%;
  gap: 6px 12px;
  align-items: center;
`;

const CollapsedTitleRow = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CollapsedTitle = styled.h3`
  margin: 0;
  line-height: 1.2;
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
`;

const CollapsedFloor = styled.span`
  font-size: 17px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.02em;
  flex-shrink: 0;
`;

const CollapsedMeta = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
`;

const CollapsedDot = styled.span`
  color: #d1d5db;
  font-size: 14px;
  font-weight: 400;
  user-select: none;
`;

const CollapsedSubtitle = styled.div`
  grid-column: 1;
  grid-row: 2;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding-left: 18px; /* align under title text (status dot + gap) */
`;

const CollapsedApartmentId = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
`;

const CollapsedDirection = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #9ca3af;
  white-space: nowrap;
`;

const CollapsedRightColumn = styled.div`
  grid-column: 2;
  grid-row: 1 / 3;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  align-self: center;
`;

const StatusIndicator = styled.div<{ status: ApartmentStatus }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${props => {
    switch (props.status) {
      case 'available': return '#10b981'; // Grün für verfügbar
      case 'reserved': return '#f39c12'; // Orange für reserviert
      case 'sold': return '#ef4444'; // Rot für verkauft
      default: return '#10b981';
    }
  }};
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
`;

const CollapsedPrice = styled.div<{ status: ApartmentStatus }>`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.status === 'available' ? '#111827' : props.status === 'reserved' ? '#f39c12' : '#ef4444'};
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (max-width: 380px) {
    font-size: 14px;
  }
`;

const CollapsedStatusBadge = styled.div<{ status: ApartmentStatus }>`
  background-color: ${props => {
    switch (props.status) {
      case 'available': return '#10b981';
      case 'reserved': return '#f39c12';
      case 'sold': return '#ef4444';
      default: return '#10b981';
    }
  }};
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const ExpandIcon = styled.div<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: transform 0.3s ease;
  transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  flex-shrink: 0;
`;



const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const ExpandedDirection = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: -4px 0 12px 0;
`;

const Price = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
`;

const StatusPrice = styled.div<{ status: ApartmentStatus }>`
  font-size: 22px;
  font-weight: 700;
  color: ${props => {
    switch (props.status) {
      case 'reserved': return '#f39c12'; // Orange für reserviert
      case 'sold': return '#ef4444'; // Rot für verkauft
      default: return '#9ca3af';
    }
  }};
  margin-bottom: 16px;
`;

const Details = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  font-size: 16px;
  color: #374151;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  
  @media (max-width: 850px) {
    font-size: 17px;
    gap: 16px;
    font-weight: 600;
  }
`;

const Detail = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 850px) {
    gap: 10px;
  }
`;

const DetailIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6b7280;
  
  @media (max-width: 850px) {
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const Features = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
`;

const Feature = styled.span`
  background-color: #f3f4f6;
  color: #4b5563;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DetailsButton = styled.button`
  width: 100%;
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #1d4ed8;
  }
`;

const DeselectButton = styled.button`
  width: 100%;
  background-color: #dc2626;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background-color: #b91c1c;
  }

  &:active {
    background-color: #991b1b;
  }
`;

const ApartmentCard: React.FC<ApartmentCardProps> = ({ 
  apartment, 
  isSelected, 
  onClick, 
  onHover, 
  isMultiSelectMode = false, 
  onDeselect 
}) => {
  // Mobile detection state
  const [isMobileView, setIsMobileView] = useState(false);
  // Expanded state for mobile collapsed/expanded view
  const [isExpanded, setIsExpanded] = useState(false);

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 850;
      setIsMobileView(isMobile);
    };

    // Check on mount
    checkMobile();

    // Listen for window resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper functions for status management
  const getStatusText = (status: ApartmentStatus): string => {
    switch (status) {
      case 'available': return 'Verfügbar';
      case 'reserved': return 'Reserviert';
      case 'sold': return 'Verkauft';
      default: return 'Verfügbar';
    }
  };

  // Helper function to get apartment status (with backward compatibility)
  const getApartmentStatus = (apartment: any): ApartmentStatus => {
    if (apartment.status) {
      return apartment.status;
    }
    // Backward compatibility: convert available boolean to status
    return apartment.available ? 'available' : 'reserved';
  };

  // Zentrale Preisformatierung wird jetzt verwendet

  const formatSize = (size: number) => {
    return `${size} m²`;
  };

  const getFloorLabel = (floor: number) => {
    if (floor === 0) return 'EG';
    if (floor === 3) return 'Attika';
    return `${floor}. Stock`;
  };

  const parseApartmentName = (name: string): { number: string; direction: string | null } => {
    const withDirection = name.match(/Wohnung\s+(.+?)\s*-\s*(.+)$/);
    if (withDirection) {
      return {
        number: withDirection[1].trim(),
        direction: withDirection[2].trim(),
      };
    }

    const simple = name.match(/Wohnung\s+(.+)$/);
    if (simple) {
      return { number: simple[1].trim(), direction: null };
    }

    return { number: name, direction: null };
  };

  const getCleanApartmentName = (name: string) => {
    const { number } = parseApartmentName(name);
    return `Wohnung ${number}`;
  };

  const handleDeselect = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDeselect) {
      onDeselect(apartment);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (isMobileView) {
      // On mobile, toggle expand/collapse
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    } else {
      // On desktop, normal behavior
      onClick(apartment);
    }
  };

  const handleDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(apartment);
  };

  const apartmentStatus = getApartmentStatus(apartment);
  const isCollapsed = isMobileView && !isExpanded;
  const { number: apartmentNumber, direction: apartmentDirection } = parseApartmentName(apartment.name);

  return (
    <Card
      isSelected={isSelected}
      isExpanded={isExpanded}
      isMobile={isMobileView}
      onClick={handleCardClick}
      onMouseEnter={() => !isMobileView && onHover?.(apartment)}
      onMouseLeave={() => !isMobileView && onHover?.(null)}
    >
      {/* Thumbnail Image with Status Badge - only show when expanded or on desktop */}
      {apartment.thumbnail && !isCollapsed && (
        <ThumbnailImage src={apartment.thumbnail} isCollapsed={isCollapsed}>
          <Status status={apartmentStatus}>
            {getStatusText(apartmentStatus)}
          </Status>
        </ThumbnailImage>
      )}
      
      <CardContent isCollapsed={isCollapsed}>
        {/* Collapsed View (Mobile Only) */}
        {isCollapsed ? (
          <CollapsedHeader>
            <CollapsedTitleRow>
              <StatusIndicator status={apartmentStatus} />
              <CollapsedTitle>
                <CollapsedFloor>{getFloorLabel(apartment.floor)}</CollapsedFloor>
                <CollapsedDot>·</CollapsedDot>
                <CollapsedMeta>{formatSize(apartment.size)}</CollapsedMeta>
                <CollapsedDot>·</CollapsedDot>
                <CollapsedMeta>{apartment.rooms} Zi.</CollapsedMeta>
              </CollapsedTitle>
            </CollapsedTitleRow>
            <CollapsedSubtitle>
              <CollapsedApartmentId>Wohnung {apartmentNumber}</CollapsedApartmentId>
              {apartmentDirection && (
                <>
                  <CollapsedDot>·</CollapsedDot>
                  <CollapsedDirection>{apartmentDirection}</CollapsedDirection>
                </>
              )}
            </CollapsedSubtitle>
            <CollapsedRightColumn>
              {apartmentStatus === 'available' ? (
                <CollapsedPrice status={apartmentStatus}>{formatPrice(apartment.price)}</CollapsedPrice>
              ) : (
                <CollapsedStatusBadge status={apartmentStatus}>
                  {apartmentStatus === 'reserved' ? 'Reserviert' : 'Verkauft'}
                </CollapsedStatusBadge>
              )}
              <ExpandIcon isExpanded={isExpanded}>
                <ChevronDown size={20} />
              </ExpandIcon>
            </CollapsedRightColumn>
          </CollapsedHeader>
        ) : (
          /* Expanded View (Mobile & Desktop) */
          <>
            <Title>
              {isMobileView ? (
                <>
                  {getFloorLabel(apartment.floor)}
                  <span style={{ color: '#d1d5db', fontWeight: 400, margin: '0 4px' }}>·</span>
                  {getCleanApartmentName(apartment.name)}
                </>
              ) : (
                getCleanApartmentName(apartment.name)
              )}
            </Title>
            {isMobileView && apartmentDirection && (
              <ExpandedDirection>Ausrichtung: {apartmentDirection}</ExpandedDirection>
            )}
            
            {apartmentStatus === 'available' ? (
              <Price>{formatPrice(apartment.price)}</Price>
            ) : (
              <StatusPrice status={apartmentStatus}>
                {apartmentStatus === 'reserved' ? 'Reserviert' : 'Verkauft'}
              </StatusPrice>
            )}

            <Details>
              <Detail>
                <DetailIcon><Building2 size={isMobileView ? 20 : 16} /></DetailIcon>
                {formatSize(apartment.size)}
              </Detail>
              <Detail>
                <DetailIcon><BedDouble size={isMobileView ? 20 : 16} /></DetailIcon>
                {apartment.rooms} Zimmer
              </Detail>
              {!isMobileView && (
                <Detail>
                  <DetailIcon><MapPin size={16} /></DetailIcon>
                  {getFloorLabel(apartment.floor)}
                </Detail>
              )}
            </Details>

            {apartment.features && apartment.features.length > 0 && (
              <Features>
                {apartment.features.slice(0, 3).map((feature, index) => (
                  <Feature key={index}>
                    <Info size={12} />
                    {feature}
                  </Feature>
                ))}
                {apartment.features.length > 3 && (
                  <Feature>
                    +{apartment.features.length - 3} weitere
                  </Feature>
                )}
              </Features>
            )}
            
            <DetailsButton onClick={handleDetailsClick}>
              Details ansehen <ChevronRight size={16} />
            </DetailsButton>

            {/* Deselect Button - only show in multi-select mode when selected and not on mobile */}
            {isMultiSelectMode && isSelected && onDeselect && !isMobileView && (
              <DeselectButton onClick={handleDeselect}>
                <X size={16} />
                Abwählen
              </DeselectButton>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ApartmentCard;
